import { Form, Input } from 'antd';
import React, {
  FC, ReactNode, useEffect, useState,
} from 'react';
import {
  Link, Navigate, useLocation, useNavigate,
} from 'react-router-dom';
import moment from 'moment';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import {
  addBookingQuestionAndAnswersData, addConsentsData, resetBookingData, selectBookingData,
} from '../../redux/slices/booking';
import {
  BookingDataState, ClientData, Consents, HealthSummaryQuestionMapping, RequestStatus,
} from '../../model';
import { CoverType, MedicareCard } from '../../model/MedicareCard';
import { bookingAppointment, isCoverComplete, needsMedicareClaimingTerm } from '../../services/booking';
import { TERMS, TermKey } from '../../constants/consents';
import {
  CLIENT_DETAILS, DOCTORS_ROUTE, MEDICARE_AND_HEALTH, THANKS,
} from '../../constants/routes';
import { DATE_WITH_DASH, TABLE_TIME } from '../../constants/time-formats';
import {
  StepError, StepLead, StepPage, StepSection,
} from '../../components/StepPanel';
import { useAppointmentSummary } from '../../components/BookingSummary';
import { useFeeEstimate } from '../../components/FeeCard';
import { formatRange } from '../../utils/consultation-fee';
import { formatTimezone } from '../../utils/timezone';
import analytics from '../../infra/analytics';
import { CONFIRM_BOOKING, PAGE_VIEW_EVENT, SITE_NAME } from '../../constants/analytics';
import Term from './Term';
import FooterNavigator from '../../components/FooterNavigator';
import { TELECARE_PHONE } from '../../constants/contact';

const ReviewCard: FC<{ title: string, editTo: string }> = ({ title, editTo, children }) => (
  <div className="rv">
    <div className="rv-head">
      <h4>{title}</h4>
      <Link className="btn-edit" to={editTo} aria-label={`Edit ${title.toLowerCase()}`}>Edit</Link>
    </div>
    <dl>{children}</dl>
  </div>
);

const Item: FC<{ label: string, children: ReactNode }> = ({ label, children }) => (
  <>
    <dt>{label}</dt>
    <dd>{children || '—'}</dd>
  </>
);

const formatMedicareNumber = (number = '') => number.replace(/^(\d{4})(\d{5})(\d)$/, '$1 $2 $3');

const coverRows = (card: Partial<MedicareCard>): [string, string] => {
  switch (card.coverType) {
    case CoverType.MEDICARE:
      return ['Medicare card', `${formatMedicareNumber(card.medicareCardNumber)} · IRN ${card.medicareCardIRN} · exp ${moment(card.expiry, DATE_WITH_DASH).format('MM/YYYY')}`];
    case CoverType.DVA:
      return ['DVA card', `${(card.dva?.cardType ?? '').replace(/^./, (c) => c.toUpperCase())} · file number ${card.dva?.cardNumber}`];
    case CoverType.MEDICARE_NOT_WITH_ME:
      return ['Medicare card — not with me', 'You’ll give us the card before your appointment'];
    case CoverType.NONE:
      return ['No Medicare or DVA card', 'Private fee applies'];
    default:
      return ['—', '—'];
  }
};

const Review: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [form] = Form.useForm();
  const bookingData = useAppSelector(selectBookingData) as BookingDataState;
  const client = (bookingData.client ?? {}) as ClientData;
  const medicareCard = (bookingData.medicareCard ?? {}) as Partial<MedicareCard>;
  const summary = useAppointmentSummary();
  const estimate = useFeeEstimate();

  const termKeys = (Object.keys(TERMS) as TermKey[])
    .filter((key) => key !== 'isAgreedTheMedicareClaiming' || needsMedicareClaimingTerm(medicareCard.coverType));
  const [agreed, setAgreed] = useState<Partial<Record<TermKey, boolean>>>(() => Object.fromEntries(
    termKeys.map((key) => [key, Boolean(bookingData.consents?.[key]?.agreed)]),
  ));
  const [open, setOpen] = useState<Partial<Record<TermKey, boolean>>>({});
  const [termsError, setTermsError] = useState(false);
  const [isHuman, setIsHuman] = useState(false);
  const [robotError, setRobotError] = useState(false);
  const [status, setStatus] = useState(RequestStatus.DEFAULT);

  useEffect(() => {
    analytics.logPageViewEvent(PAGE_VIEW_EVENT, {
      site: SITE_NAME,
      page_name: CONFIRM_BOOKING,
    });
  }, []);

  const gapText = estimate ? formatRange(estimate.gap) : null;
  const allAgreed = termKeys.every((key) => agreed[key]);

  const handleAgree = (key: TermKey, value: boolean) => {
    setAgreed((prev) => ({ ...prev, [key]: value }));
    // Kept in the booking so ticks survive going to an Edit page and back.
    const { name, paragraphs } = TERMS[key];
    dispatch(addConsentsData({ [key]: { name, description: paragraphs.join('\n'), agreed: value } }));
    if (value) setTermsError(false);
  };

  const handleConfirm = async () => {
    if (!allAgreed) {
      setTermsError(true);
      setOpen(Object.fromEntries(termKeys.filter((key) => !agreed[key]).map((key) => [key, true])));
      return;
    }
    if (!isHuman) {
      setRobotError(true);
      return;
    }

    // What the patient agreed to, as shown. Only the terms on screen are sent, so a term ticked
    // before the cover changed is not recorded.
    const consents: Consents = {
      isAgreedThePrivacyPolicy: bookingData.consents?.isAgreedThePrivacyPolicy,
      ...Object.fromEntries(termKeys.map((key) => {
        const { name, paragraphs } = TERMS[key];
        return [key, { name, description: paragraphs.join('\n'), agreed: true }];
      })),
    };
    const { howHear } = form.getFieldsValue();
    const questionAndAnswers = {
      ...bookingData.questionAndAnswers,
      howHear: howHear?.trim() || undefined,
      consultWay: 'video',
    };
    dispatch(addConsentsData(consents));
    dispatch(addBookingQuestionAndAnswersData(questionAndAnswers));

    setStatus(RequestStatus.LOADING);
    const result = await bookingAppointment({ ...bookingData, consents, questionAndAnswers });
    setStatus(result.status);
    if (result.status === RequestStatus.SUCCESS) {
      // The booking is cleared, so the confirmation page is handed what it shows.
      navigate(THANKS, {
        state: {
          ...summary,
          timezone: formatTimezone(bookingData.timezone, bookingData.appointment?.date),
          coverType: medicareCard.coverType,
        },
      });
      dispatch(resetBookingData());
    }
  };

  // Cover picked but not finished (e.g. swiped back from the Medicare step): finish it there first.
  if (status !== RequestStatus.SUCCESS && !isCoverComplete(medicareCard as MedicareCard)) {
    return <Navigate to={`${MEDICARE_AND_HEALTH}${search}`} replace />;
  }

  const [coverLabel, coverDetail] = coverRows(medicareCard);

  return (
    <StepPage>
      <h2>Check your details and agree to the terms</h2>
      <StepLead>Nothing is charged on this page. Your payment link arrives by SMS after you book.</StepLead>

      <StepSection>
        <h3>Your appointment</h3>
        <ReviewCard title="Appointment" editTo={`${DOCTORS_ROUTE}${search}`}>
          <Item label="Clinician">{summary.clinician}</Item>
          <Item label="Specialty">{summary.specialty}</Item>
          <Item label="Date & time">{summary.when}</Item>
          <Item label="Timezone">{formatTimezone(bookingData.timezone, bookingData.appointment?.date)}</Item>
        </ReviewCard>
        <ReviewCard title="Patient" editTo={`${CLIENT_DETAILS}${search}`}>
          <Item label="Name">{[client.firstName, client.lastName].filter(Boolean).join(' ')}</Item>
          <Item label="Date of birth">{client.dateOfBirth && moment(client.dateOfBirth, DATE_WITH_DASH).format(TABLE_TIME)}</Item>
          <Item label="Mobile">{client.mobile}</Item>
          <Item label="Email">{client.email}</Item>
        </ReviewCard>
        <ReviewCard title="Cover" editTo={`${MEDICARE_AND_HEALTH}${search}`}>
          <Item label="Cover">{coverLabel}</Item>
          <Item label="Details">{coverDetail}</Item>
        </ReviewCard>
      </StepSection>

      <StepSection>
        <h3>Terms — please open, read and tick each one</h3>
        {termKeys.map((key) => (
          <Term
            key={key}
            title={TERMS[key].title}
            agreed={Boolean(agreed[key])}
            open={Boolean(open[key])}
            onAgreedChange={(value) => handleAgree(key, value)}
            onOpenChange={(value) => setOpen((prev) => ({ ...prev, [key]: value }))}
          >
            {TERMS[key].paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </Term>
        ))}
        {termsError && <StepError role="alert">Please open, read and tick each term above before continuing.</StepError>}
      </StepSection>

      <StepSection>
        <Form form={form} layout="vertical">
          <Form.Item label={HealthSummaryQuestionMapping.howHear} name="howHear" initialValue={bookingData.questionAndAnswers?.howHear}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
        <ReCAPTCHA
          sitekey={process.env.REACT_APP_RECAPTCHA_KEY as string}
          onChange={(value) => {
            setIsHuman(Boolean(value));
            if (value) setRobotError(false);
          }}
        />
        {robotError && <StepError role="alert">Please tick “I’m not a robot” before confirming.</StepError>}
      </StepSection>

      {status === RequestStatus.FAILURE && (
        <div className="notice n-err" role="alert">
          {`We could not complete your booking. Please try again, or call Telecare on ${TELECARE_PHONE}.`}
        </div>
      )}

      <FooterNavigator
        back={MEDICARE_AND_HEALTH}
        onNext={handleConfirm}
        label="Confirm booking"
        busy={status === RequestStatus.LOADING || status === RequestStatus.SUCCESS}
        disabled={!allAgreed || !isHuman}
        hint={(!allAgreed && 'Tick each term to continue') || (!isHuman && 'Tick “I’m not a robot” to continue') || undefined}
        middle={gapText && (
          <div className="feeline">
            Out of pocket (gap fee) · estimate
            <b>{gapText}</b>
          </div>
        )}
      />
    </StepPage>
  );
};

export default Review;
